import { QueryClient, useQuery, useQueryClient, UseQueryResult } from 'react-query';
import { User } from 'src/types/User';
import { getPage, Page, PageRequest, sortValueFrom } from '../pagination';

export enum UserQueryKeys {
  UserPage = 'UserPage',
}

/** Sort fields the user search accepts. Mirrors the API's USER_SORT_FIELDS. */
export const USER_SORT_FIELDS = ['email', 'firstName', 'lastName', 'createdAt'] as const;

/** How a row is ordered by each of {@link USER_SORT_FIELDS}. */
export const getUserSortValue = sortValueFrom<User>({
  email: (user) => user.email,
  firstName: (user) => user.firstName,
  lastName: (user) => user.lastName,
  createdAt: (user) => user.createdAt,
});

/**
 * ONE page of the platform-wide user search, with the total.
 *
 * The screens this replaces asked for `?email=%` — every user on the platform — and
 * rendered all of them: the User List put them in a table, and the "Add Existing User"
 * picker put the same list in an Autocomplete. Neither could say how many users there
 * were, because the endpoint only ever answered with an array.
 */
export function getUsersPage(request: PageRequest, queryClient?: QueryClient): Promise<Page<User>> {
  return getPage<User>(
    'user',
    request,
    // `email: '%'` is the FLAG-OFF fallback, and it is load-bearing rather than legacy
    // residue. While ORG_SCALE_PAGINATION is off the server ignores the paging parameters
    // and takes its legacy branch — which REQUIRES an email and answers 400 without one.
    // A wildcard is what that branch has always been given by this screen, so the flag-off
    // response is the same full list it serves today, which getPage then searches, sorts
    // and windows. The paged branch ignores `email` in favour of `search`.
    { email: '%' },
    {
      // The legacy branch matches on email alone. Without these, the search box would
      // answer with the first twenty users on the platform while the flag is off — not an
      // error and not an empty state, a confident wrong answer.
      searchable: (user) => [user.id, user.email, user.firstName, user.lastName],
      sortValue: getUserSortValue,
    },
    queryClient,
    // The same base key `useUsersPage` queries under, so anything that already invalidates
    // the user list reaches the shared whole-list copy too.
    [UserQueryKeys.UserPage]
  );
}

/** One page of users, as a query. */
export function useUsersPage(
  request: PageRequest,
  options?: { enabled?: boolean }
): UseQueryResult<Page<User>, Error> {
  // Shares the FLAG-OFF whole-list download across pages, sorts and keystrokes — without
  // it every one of those is a new react-query key over the same platform-wide user array,
  // and each one re-downloads it. The customer and organisation page hooks already do this;
  // this one was the adopter that did not.
  const queryClient = useQueryClient();
  return useQuery<Page<User>, Error>(
    [UserQueryKeys.UserPage, request],
    () => getUsersPage(request, queryClient),
    {
      enabled: options?.enabled ?? true,
      // The rows stay while the next page loads, so paging dims the table instead of
      // emptying it.
      keepPreviousData: true,
    }
  );
}
